import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Input, input, OnDestroy, output, type OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { InputParameter } from '../../models/engineering/input-parameter';
import { Parameter } from '../../models/engineering/parameter';
import { ModalService } from '@card-software/library';
import { ParameterValuesModalComponent } from '../modals/step-values-modal/parameter-values-modal.component';
import _ from 'lodash';
import { ParameterProxyService } from '../../proxies/item/parameter.proxy.service';
import { Router } from '@angular/router';
import { Step } from '../../models/engineering/step';
import { Process } from '../../models/engineering/process';
import { parameterValueTypeValidator, getParameterTypeErrorMessage } from '../../validators/parameter-type.validator';

@Component({
  selector: 'app-general-additional-parameters',
  templateUrl: './general-additional-parameters.component.html',
  styleUrl: './general-additional-parameters.component.scss',
})
export class GeneralAdditionalParametersComponent implements OnInit, OnDestroy {
  @Input() canEdit = false;
  //*Emitters ans inputs
  public additionalParametersEmitter = output<
    FormArray<
      FormGroup<{
        isActive: FormControl<boolean>;
        parameterRef: FormControl<string>;
        hasMultipleValues: FormControl<boolean>;
        isRequired: FormControl<boolean>;
        values: FormArray<FormControl<string>>;
      }>
    >
  >();
  public records = input<Step | Process | null>(null);

  getParameterTypeErrorMessage = getParameterTypeErrorMessage;

  //*Injections
  private modalService = inject(ModalService);
  private parameterProxyService = inject(ParameterProxyService);
  private router: Router = inject(Router);

  //* properties
  public showInstructions = false;
  public allParameters: Parameter[] = [];

  //* Handle form-controls
  public additionalParameters = new FormArray<
    FormGroup<{
      isActive: FormControl<boolean>;
      parameterRef: FormControl<string>;
      hasMultipleValues: FormControl<boolean>;
      isRequired: FormControl<boolean>;
      values: FormArray<FormControl<string>>;
    }>
  >([]);
  public name = new FormControl('', [Validators.required]);
  public formGroup = new FormGroup({
    name: this.name,
    additionalParameters: this.additionalParameters,
  });

  ngOnInit(): void {
    this.parameterProxyService.getParameters().subscribe({
      next: (response) => {
        this.allParameters = response.data || [];
        this.populateForm();
      },
      error: () => {
        //alert('Failed to load parameters');
        this.router.navigate(['/steps']);
      },
    });

    //these are the values that are being emitted when user change of inputs and valid if these not empty
    this.additionalParameters.valueChanges.subscribe(() => {
      this._emitData();
    });
  }

  ngOnDestroy(): void {
    this.additionalParameters.clear();
  }

  public addParameter() {
    const paramGroup = this.createParameterGroup();
    this.additionalParameters.push(paramGroup);
  }

  private handleParameterRefChange(paramGroup: FormGroup) {
    const parameterRefControl = paramGroup.get('parameterRef') as FormControl;
    const valuesControl = paramGroup.get('values') as FormArray;
    const hasMultipleValuesControl = paramGroup.get('hasMultipleValues') as FormControl<boolean>;

    parameterRefControl.valueChanges.subscribe((value) => {
      valuesControl.clear();

      if (!value) {
        valuesControl.disable(); // Disable if empty
      } else {
        valuesControl.enable(); // Enable when a parameterRef is selected
        const parameter = this.allParameters.find((param) => param.id === value);

        if (hasMultipleValuesControl.value) {
          // Multiple values, keep an empty array for user input
          valuesControl.setValue([]);
        } else {
          // Single value, add an empty field with required validator
          valuesControl.push(new FormControl('', [parameterValueTypeValidator(parameter!.parameterType)]));
        }
      }
      this._emitData();
    });
  }

  public createParameterGroup(param?: InputParameter): FormGroup {
    const paramGroup = new FormGroup({
      isActive: new FormControl(param?.isActive ?? true),
      parameterRef: new FormControl(param?.parameterRef || '', [Validators.required]),
      hasMultipleValues: new FormControl(param?.hasMultipleValues || false),
      isRequired: new FormControl(param?.isRequired || false),
      values: new FormArray(
        (param?.values?.length ? param.values : param?.hasMultipleValues ? [] : param?.parameterRef ? [''] : []).map(
          (value: string) => new FormControl(value, param?.hasMultipleValues ? [Validators.required] : []),
        ),
      ),
    });

    this.handleParameterRefChange(paramGroup);
    return paramGroup;
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.additionalParameters.controls, event.previousIndex, event.currentIndex);
    this.additionalParameters.updateValueAndValidity();
  }

  dropAdditionalParams(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.additionalParameters.controls, event.previousIndex, event.currentIndex);
    this.additionalParameters.updateValueAndValidity();
  }

  public openStepValuesModal(formGroupIndex: number) {
    const formGroup = this.additionalParameters.controls[formGroupIndex];
    const originalFormArray = formGroup.controls['values'] as FormArray;

    const clonedFormArray = new FormArray(_.cloneDeep(originalFormArray.controls).map((control) => new FormControl(control.value)));
    const parameterType = this.allParameters.find((param) => param.id === formGroup.controls['parameterRef'].value)?.parameterType;

    this.modalService
      .open(ParameterValuesModalComponent, {}, { stepValues: clonedFormArray, parameterType: parameterType })
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          formGroup.setControl('values', result.data);
        }
      });
  }

  // set records to the form
  public populateForm() {
    this.name.setValue(this.records()?.name.en || '');
    console.log('Populating additional parameters:', this.records()?.additionalParameters);
    this.records()?.additionalParameters.forEach((param) => {
      const paramGroup = this.createParameterGroup(param);
      this.additionalParameters.push(paramGroup);
    });
    //Disable form by Rol
    if (!this.canEdit) {
      this.additionalParameters.disable();
      this.name.disable();
    }
  }

  public resetParamValues(parameterGroupFormIndex: number) {
    const formGroup = this.additionalParameters.controls[parameterGroupFormIndex];
    const valuesControl = formGroup.controls.values;
    if ((formGroup.controls['hasMultipleValues'] as FormControl<boolean>).value) {
      valuesControl.clear();
    } else {
      valuesControl.clear();
      valuesControl.push(new FormControl());
    }
  }

  public removeParameter(index: number) {
    this.additionalParameters.removeAt(index);
  }

  // emit data for the parent component
  private _emitData() {
    this.additionalParametersEmitter.emit(this.additionalParameters);
  }
}
